import { createSelector } from '@reduxjs/toolkit';

import rootCategories, { ENTERTAINING, GROCERIES } from 'constants/categoryIds';
import { urls } from 'constants/urls';

import {
  getAccountMenu,
  getSiteLinks,
  getData as getTaxonomyData,
  getCategoryById,
  createCategoryUrlResolver,
} from 'redux/modules/taxonomy/selectors';

import decorateSubcategoryMenu from './decorate-subcategory-menu';

const getMegaMenu = ({ megaMenu } = {}) => megaMenu;

export const getMegaMenuActiveItemId = createSelector(
  getMegaMenu,
  ({ activeItem } = {}) => activeItem,
);

const DEFAULT_MEGA_MENU_HISTORY = [];
export const getMegaMenuHistory = createSelector(
  getMegaMenu,
  ({ history } = {}) => history || DEFAULT_MEGA_MENU_HISTORY,
);

export const getMegaMenuRoot = createSelector(getMegaMenu, ({ menuRoot } = {}) => menuRoot);

export const getMegaMenuOpen = createSelector(getMegaMenu, ({ open = false } = {}) => open);

const getUrlForActiveMegaMenuLevel = createSelector(
  [getMegaMenu, createCategoryUrlResolver],
  (megaMenuState, getCategoryUrlSegment) => {
    const { activeItem, menuRoot } = megaMenuState || {};
    if (menuRoot === activeItem) {
      return getCategoryUrlSegment(activeItem);
    }

    return [getCategoryUrlSegment(menuRoot), getCategoryUrlSegment(activeItem)].join('/');
  },
);

const getPathForMegaMenu = createSelector(
  [getMegaMenuHistory, getUrlForActiveMegaMenuLevel, createCategoryUrlResolver],
  (history, activeUrl, getCategoryUrlSegment) => {
    if (history.length === 0) {
      return activeUrl;
    }

    const rootSegment = getCategoryUrlSegment(history[0].menuRoot);
    const historySegments = history.map(({ activeItem }) => getCategoryUrlSegment(activeItem));

    return [rootSegment, ...historySegments].join('/');
  },
);

const buildOffersPathFromHistory = createSelector(
  [getMegaMenuHistory, createCategoryUrlResolver],
  (history, getCategoryUrlSegment) =>
    [urls.offers]
      .concat(
        history
          .filter(({ activeItem }) => !Object.values(rootCategories).includes(activeItem))
          .map(({ activeItem }) => `${getCategoryUrlSegment(activeItem)}_offers`),
      )
      .join('/'),
);

const getOffersPath = createSelector(
  [getMegaMenuRoot, buildOffersPathFromHistory],
  (menuRoot, pathname) => ({
    pathname,
    search: menuRoot === GROCERIES ? null : `?categoryId=${menuRoot}`,
  }),
);

const getMegaMenuActiveMenu = state => {
  let menuRoot = getMegaMenuRoot(state);

  // If no menuRoot is specified, default to MEGA_MENU for cms menus
  if (!menuRoot) {
    const megaMenu = getMegaMenu(state);
    if (megaMenu?.cmsMenus?.length > 0) {
      menuRoot = 'MEGA_MENU';
    }
  }

  if (!menuRoot) return undefined;

  const history = getMegaMenuHistory(state);
  const path = getPathForMegaMenu(state);
  const offersPath = getOffersPath(state);

  const [{ activeItem: subCategoryRoot } = {}] = history;
  const hasOffersLink = history.length < 3;
  let activeMenu = getCategoryById(state, menuRoot);

  if (subCategoryRoot === GROCERIES) {
    activeMenu = decorateSubcategoryMenu(activeMenu, path, hasOffersLink && offersPath);
  }

  return activeMenu;
};

export const getPreviousActiveTitle = state => {
  const history = getMegaMenuHistory(state);
  const { menuRoot: previousActiveItem } = history[history.length - 1] || {};
  const category = previousActiveItem ? getCategoryById(state, previousActiveItem) : {};
  return category?.name;
};

const DEFAULT_NAVIGATION_MENUS = [];
export const getNavigationMenus = createSelector(
  [getMegaMenuHistory, getMegaMenuActiveMenu, getAccountMenu, getSiteLinks, getMegaMenu],
  (history, activeMenu, accountMenu, siteLinks, megaMenuState) => {
    const level = megaMenuState?.level !== undefined ? megaMenuState.level : history.length;
    let result;
    if (level === 0) {
      result = [activeMenu, accountMenu, siteLinks].filter(menuItem => menuItem);
    } else {
      result = [activeMenu].filter(menuItem => menuItem);
    }

    return result.length ? result : DEFAULT_NAVIGATION_MENUS;
  },
);

export const getActiveItem = state => {
  const activeItem = getMegaMenuActiveItemId(state);
  return activeItem ? getCategoryById(state, activeItem) : activeItem;
};

export const getPathForMegaMenuItemById = createSelector(
  [
    (state, id) => id,
    getMegaMenuHistory,
    getPathForMegaMenu,
    getUrlForActiveMegaMenuLevel,
    createCategoryUrlResolver,
  ],
  (id, history, path, activeUrl, getCategoryUrlSegment) => {
    if (id === ENTERTAINING) return urls.entertainingHome;
    // TODO: the array we're creating on the next line could be memoized to avoid recreating the array and unnecessary rerenders
    return history.length === 0 ? activeUrl : [path, getCategoryUrlSegment(id)].join('/');
  },
);

const DEFAULT_CURRENT_MENU_LIST = [];
export const getCurrentMenuList = createSelector(
  [getNavigationMenus, getTaxonomyData],
  (navigationMenus, taxonomyData) => {
    const result = []
      .concat(...navigationMenus.map(({ categoryIds = [] }) => categoryIds))
      .map(category => (typeof category === 'object' && category !== null ? category.id : category))
      .filter(categoryId => categoryId)
      .filter(categoryId => {
        const { emptyOfProducts = false } = taxonomyData[categoryId] || {};
        return !emptyOfProducts;
      });
    return result.length ? result : DEFAULT_CURRENT_MENU_LIST;
  },
);

export const getExperienceFragmentKeyForActiveMenuItem = state => {
  const activeItem = getActiveItem(state);
  return activeItem?.banner;
};
